/**
 * THE ADDRESS CARRIES THE BASKET, INSIDE AN APP'S BROWSER (2026-10-25).
 *
 * A buyer on 2026-10-21 used the app's OWN menu (··· on iOS, ⋮ on Android) → "Open in browser" and arrived in Safari
 * with an empty basket: that menu opens the address the page has RIGHT NOW, and the basket lived only in the app's
 * localStorage. So inside an app's browser the page keeps the basket in the address bar — `items=…&handoff=1` (plus the
 * ad the visit came from) — and whatever the app opens carries it.
 *
 * NEVER A TOKEN IN THE ADDRESS (2026-10-25 ruling). An earlier build also wrote `handoff=<token>` here once the
 * checkout had kept the shopper's name, phone and address on the server. The app's own Copy link / Share would then
 * hand those to whoever opened the link within 15 minutes, and a token that aged out left an empty basket in Safari.
 * The address carries ids and counts the shop already publishes, and nothing the shopper typed. The TAP on the
 * checkout's "open in browser" line still mints its own token and leaves with it (Pages/Checkout, openInBrowser).
 * A real browser reached with those parameters restores the basket (Components/InAppBasketAddress).
 *
 * The address is rewritten with `history.replaceState`, which hands the page's CURRENT `history.state` back
 * unchanged: the router's own bookkeeping and the back-swipe guard's `{ stylonInAppGuard: true }` (inAppBackGuard.js)
 * live there, and a replace that dropped them would break the back button. No navigation, no redirect, no reload.
 */
import { HANDOFF_FLAG_PARAM, HANDOFF_ITEMS_PARAM, encodeHandoffItems } from './openInBrowser';

/** The ad refs `handoffUrl` writes, by the same names, so the real browser recognises the landing as the same click's. */
const AD_CAMPAIGN_PARAM = 'stylon_cid';
const AD_REF_PARAM = 'stylon_ad';

/**
 * The path plus a query that keeps every parameter except `items` and `handoff`. With lines: `items=…&handoff=1`,
 * then the ad refs of `adVisit` (`currentAdVisit()`'s shape) when the visit has them; with none, neither is added.
 * The visitor key never rides here: it is the shopper's identity, and this address can be copied.
 */
export function addressWithBasket({ pathname, search, items, adVisit = null }) {
  const encoded = encodeHandoffItems(items);
  const withAd = !!(encoded && adVisit && (adVisit.campaignId || adVisit.platformAdRef));
  let kept = '';
  try {
    const q = new URLSearchParams(search || '');
    q.delete(HANDOFF_ITEMS_PARAM);
    q.delete(HANDOFF_FLAG_PARAM);
    if (withAd) { q.delete(AD_CAMPAIGN_PARAM); q.delete(AD_REF_PARAM); }
    kept = q.toString();
  } catch (_) { kept = ''; }
  const parts = kept ? [kept] : [];
  if (encoded) {
    // Written by hand, as handoffUrl does: `:` and `,` stay readable in the address.
    parts.push(`${HANDOFF_ITEMS_PARAM}=${encoded}`, `${HANDOFF_FLAG_PARAM}=1`);
    if (withAd && adVisit.campaignId) parts.push(`${AD_CAMPAIGN_PARAM}=${encodeURIComponent(String(adVisit.campaignId))}`);
    if (withAd && adVisit.platformAdRef) parts.push(`${AD_REF_PARAM}=${encodeURIComponent(String(adVisit.platformAdRef))}`);
  }
  return `${pathname || '/'}${parts.length ? `?${parts.join('&')}` : ''}`;
}

/**
 * Writes the basket into the address bar of `win`, only when the address changes. Returns whether it wrote.
 * Never throws: a browser that refuses `replaceState` (too many calls, a sandbox) simply keeps the bare address.
 */
export function keepBasketInAddress(win, { items, adVisit = null } = {}) {
  try {
    const { location, history } = win;
    const next = addressWithBasket({ pathname: location.pathname, search: location.search, items, adVisit });
    if (next === `${location.pathname}${location.search}`) return false;
    history.replaceState(history.state, '', `${next}${location.hash || ''}`);
    return true;
  } catch (_) {
    return false;
  }
}
